import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useState } from 'react'

import { Button } from '@ui/components/core'
import { Notification, NotiticationsProps } from '@ui/components/core/Notification'
import { showToast } from '@ui/components/elements/ToastManager'
import { NotificationTypeEnum } from '@ui/interfaces'
import axios from '@ui/lib/api/axiosHelper'

interface InviteMemberModalProps {
  fetchMembers: () => void
  modalState: boolean
  setModalClosed: () => void
  orgId: number
}
export const InviteMemberModal = ({ fetchMembers, modalState, setModalClosed, orgId }: InviteMemberModalProps) => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)

  const [notification, setNotification] = useState<NotiticationsProps | null>(null)

  const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setName(value)
  }

  const onChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setEmail(value)
  }

  const onSubmit = (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setTimeout(createMember, 1000)
  }

  const createMember = () => {
    const nameSplit = name.split(' ')
    const firstName = nameSplit.shift()
    const lastName = nameSplit.join(' ')

    const configuration = {
      url: `/v1/organizations/${orgId}/members`,
      data: {
        email,
        first_name: firstName === '' ? null : firstName,
        last_name: lastName === '' ? null : lastName,
      },
    }

    axios
      .post(configuration.url, configuration.data)
      .then(async () => {
        showToast.success('Member has been invited')

        fetchMembers()
        setModalClosed()
        setLoading(false)
        setNotification(null)
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((err: any) => {
        setLoading(false)

        setNotification({
          show: true,
          title: 'Failed to invite member',
          message: err.response.data.error_message,
          type: NotificationTypeEnum.ERROR,
        })

        console.log('Failed to invite member: ', err)
      })
  }

  const clearInput = () => {
    setName('')
    setEmail('')
    setNotification(null)
  }

  return (
    <Transition.Root show={modalState} as={Fragment} afterLeave={clearInput}>
      <Dialog as="div" className="relative z-40" onClose={setModalClosed}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <Dialog.Panel className="sm:w-md relative w-1/2 overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      Invite user
                    </Dialog.Title>
                  </div>
                </div>
                <form className="mt-5 sm:items-center" onSubmit={onSubmit}>
                  <div className="">
                    <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                      Email
                    </label>
                    <div className="mt-2">
                      <input
                        type="email"
                        name="email"
                        id="email"
                        onChange={onChangeEmail}
                        value={email}
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                      Name
                    </label>
                    <div className="mt-2">
                      <input
                        type="text"
                        name="name"
                        id="name"
                        onChange={onChangeName}
                        value={name}
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>

                  <div className="w-full">
                    <Notification
                      show={notification !== null}
                      type={NotificationTypeEnum.ERROR}
                      title={notification?.title || ''}
                      message={notification?.message}
                      richMessage={notification?.richMessage}
                      messageList={notification?.messageList}
                    />
                  </div>

                  <div className="mt-8 flex justify-end">
                    <Button type="submit" disabled={loading} loading={loading}>
                      Add user
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      loading={loading}
                      variant="secondary"
                      onClick={setModalClosed}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
